<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import TextareaInput from "@/Components/TextareaInput.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import {Link, useForm, usePage} from '@inertiajs/inertia-vue3';
import Datepicker from "@vuepic/vue-datepicker";
import Multiselect from "vue-multiselect";
import NumberInput from "@/Components/NumberInput.vue";
import {computed, ref, watch} from "vue";
import dayjs from "dayjs";
import {Inertia} from "@inertiajs/inertia";
import ActionButton from "@/Components/ActionButton.vue";
import DisabledInput from "@/Components/DisabledInput.vue";
import {useHelpers} from "@/helpers";

const props = defineProps({
  workorder: Object,
  companies: Array,
  users: Array,
  billingCodes: Array,
  isGarmentColorWorkorderView: {
    type: Boolean,
    default: false,
  },
});

const helpers = useHelpers();
const page = usePage();
const currentUser = page.props.value.auth.user;

function openMailPreviewIfPresent() {
  const url = page.props.value?.workorder_mail_preview_url
    ?? page.props.value?.flash?.workorder_mail_preview_url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const workorderProperty = computed(() => props.workorder);
watch(workorderProperty, () => resetForm(workorderProperty.value));
const isDisabled = ref(workorderProperty?.value?.open === 'no');

const dateDisplayFormat = (date) => {
  const options = {year: 'numeric', month: 'long', day: 'numeric'};

  return date.toLocaleDateString('en-US', options);
}
const completionDate = ref((props.workorder?.completiondate && props.workorder?.completiondate > 0) ? (props.workorder?.completiondate * 1000) : null);
const form = useForm({
  _method: 'POST',
  company: props.workorder?.company || null,
  user: props.workorder?.user || null,
  billing_code: props.workorder?.billing_code || null,
  hours: props.workorder?.hours || 0,
  completiondate: completionDate.value,
  description: props.workorder?.description || null,
  jobnotes: props.workorder?.jobnotes || null,
  assigned_user_notes: props.workorder?.assigned_user_notes || null,
  is_billable: props.workorder?.is_billable || true,
});

const minDate = dayjs.tz().add(2, 'day').toDate();
const saving = ref(false);
const saveForm = () => {
  saving.value = true;
  form.completiondate = completionDate.value;
  if (completionDate.value !== null && String(completionDate.value).length > 11) { // Fix the conversion of the timestamp between PHP seconds and JS milliseconds
    form.completiondate = form.completiondate / 1000;
  }
  let url = route('admin.workorders.store');
  if (props.workorder?.ID) {
    form._method = 'PUT';
    url = route('admin.workorders.update', {workorder: props.workorder.ID});
  }
  const element = document.getElementById('company')
  form.post(url, {
    preserveScroll: true,
    onSuccess() {
      saving.value = false;
      openMailPreviewIfPresent();
      element.scrollIntoView();
    },
    onError() {
      saving.value = false;
      element.scrollIntoView();
    }
  });
}

const resetForm = (data) => {
  completionDate.value = (data?.completiondate && data?.completiondate > 0) ? (data?.completiondate * 1000) : null;
  Object.keys(form.data()).forEach(key => {
    if (key !== 'completiondate') {
      form[key] = data[key];
    }
  });
}

const getStatus = () => {
  if (!workorderProperty.value.ID) {
    return '';
  }

  return workorderProperty.value.open === 'yes' ? 'Open' : 'Closed';
}

const isAssignedByUser = () => {
  if (!workorderProperty.value?.ID) {
    return true;
  }

  return parseInt(workorderProperty.value.whoassigned) === parseInt(currentUser.employeeID);
}

const isAssignedToUser = () => {
  if (!workorderProperty.value?.ID) {
    return false;
  }

  return parseInt(workorderProperty.value.employeeID) === parseInt(currentUser.employeeID);
}

const billAndClose = () => {
  if (workorderProperty.value.open === 'no') {
    return;
  }
  saving.value = true;
  Inertia.post(route('admin.workorders.close', {workorder: workorderProperty.value.ID}), {}, {
    preserveScroll: true,
    onSuccess() {
      saving.value = false;
      openMailPreviewIfPresent();
    },
    onError() {
      saving.value = false;
    }
  });
}
const formatDate = (timestamp) => {
  if (!timestamp) {
    return '';
  }
  return dayjs.tz(timestamp * 1000).format("MMMM D, YYYY");
}

const isOpen = () => {
  return workorderProperty.value?.open === 'yes';
}

const isClosed = () => {
  return workorderProperty.value?.open === 'no';
}
</script>

<template>
  <AppLayout title="Workorders">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="workorder?.ID">Edit workorder</span>
        <span v-else>New Workorder</span>
      </h2>
    </template>

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8 bg-white rounded mb-5">
      <div class="max-w-3xl mx-auto">
        <div class="mb-5" v-if="workorderProperty?.ID">
          <InputLabel value="Workorder #"/>
          <DisabledInput :value="workorderProperty?.identifier"/>
        </div>
        <div class="mb-5">
          <InputLabel for="company" value="Company" :required="true"/>
          <Multiselect id="company" v-model="form.company" :options="companies" label="client" track-by="ID"
                       :disabled="isClosed()"/>
          <InputError :message="form.errors['company.ID']" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="user" value="Assigned User" :required="true"/>
          <Multiselect id="user" v-model="form.user" :options="users" label="full_name" track-by="employeeID"
                       :disabled="isClosed()"/>
          <InputError :message="form.errors['user.employeeID']" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="type-of-work" value="Type of Work" :required="true"/>
          <Multiselect id="type-of-work" v-model="form.billing_code" :options="billingCodes" :disabled="isClosed()"
                       :custom-label="(code) => `${code.description} - $${code.price}`" track-by="ID"/>
          <InputError :message="form.errors['billing_code.ID']" class="mt-2"/>
        </div>
        <div class="mb-5" v-if="isGarmentColorWorkorderView">
          <InputLabel for="description" value="Description"/>
          <TextareaInput
            id="description"
            v-model="form.description"
            rows="4"
            class="mt-1 block w-full"
            autocomplete="description"
            :disabled="isClosed()"
          />
          <InputError :message="form.errors.description" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="estimated-hours" value="Estimated Hours" :required="true"/>
          <NumberInput
            id="estimated-hours"
            class="mt-1 block w-full"
            v-model="form.hours"
            autocomplete="estimated-hours"
            :disabled="isClosed()"
          />
          <InputError :message="form.errors.hours" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="completion-date" value="Completion Date" :required="true"/>
          <DisabledInput :value="formatDate(workorderProperty?.completiondate)" v-if="isClosed()"/>
          <Datepicker v-else class="mt-1 block w-full shadow-sm bg-gray-50" v-model="completionDate"
                      :format="dateDisplayFormat" model-type="timestamp" auto-apply :close-on-auto-apply="true"
                      :enable-time-picker="false" :min-date="minDate"/>
          <InputError :message="form.errors.completiondate" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="do-not-bill" value="Bill the company?"/>
          <label for="do-not-bill" class="mr-4">
            <input type="radio" v-model="form.is_billable" :value="true" id="bill-yes" :disabled="isClosed()"/>
            <span class="ml-1" :class="{'text-gray-500': isClosed()}">Yes</span>
          </label>
          <label for="do-not-bill">
            <input type="radio" v-model="form.is_billable" :value="false" id="bill-no" :disabled="isClosed()"/>
            <span class="ml-1" :class="{'text-gray-500': isClosed()}">No</span>
          </label>
          <InputError :message="form.errors.jobnotes" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="notes" value="Job Notes"/>
          <TextareaInput
            :disabled="isAssignedToUser() || isClosed()"
            id="notes"
            v-model="form.jobnotes"
            rows="7"
            class="mt-1 block w-full"
            autocomplete="notes"
          />
          <InputError :message="form.errors.jobnotes" class="mt-2"/>
        </div>
        <div class="mb-5">
          <InputLabel for="assigned-user-notes" value="Assigned User Notes"/>
          <TextareaInput
            :disabled="isAssignedByUser() || isClosed()"
            id="assigned-user-notes"
            v-model="form.assigned_user_notes"
            rows="7"
            class="mt-1 block w-full"
            autocomplete="assigned-user-notes"
          />
          <InputError :message="form.errors.jobnotes" class="mt-2"/>
        </div>
        <div class="mb-5" v-if="workorderProperty?.ID">
          <InputLabel for="status" value="Status"/>
          <DisabledInput :value="getStatus()"/>
        </div>
        <div class="mb-5" v-if="workorderProperty?.ID">
          <InputLabel for="who-assigned" value="Assigned By"/>
          <DisabledInput :value="workorderProperty.assigned_by.full_name"/>
        </div>
        <div class="mb-5" v-if="workorderProperty?.ID">
          <InputLabel for="date-assigned" value="Assigned At"/>
          <DisabledInput :value="formatDate(workorderProperty.dateassigned)"/>
        </div>
        <div class="mx-auto w-full">
          <PrimaryButton @click.prevent="saveForm()" class="mr-2" :disabled="saving"
                         v-if="(!(workorderProperty?.ID) || isOpen())">
            <span v-if="!workorderProperty?.ID">Submit</span>
            <span v-else>Update</span>
          </PrimaryButton>
          <Link :href="route('admin.workorders.index')" class="mr-2">
            <SecondaryButton :disabled="saving">Back</SecondaryButton>
          </Link>
          <ActionButton v-if="workorderProperty?.ID && isOpen() && helpers.userHasPermission(currentUser, 'admin')"
                        @click.prevent="billAndClose()" :disabled="saving">
            Bill &amp; Close
          </ActionButton>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
